import AgreementTypeSelect from "@/components/AgreementTypeSelect";
import CheckboxField from "@/components/CheckboxField";
import { useDictionaryContext } from "@/components/dictionaries/DictionaryContext";
import DictionaryForm from "@/components/dictionaries/DictionaryForm";
import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import InvoicingCompanySelect from "@/components/InvoicingCompanySelect";
import PageContent from "@/components/PageContent";
import ProjectDependsOnSelect from "@/components/ProjectDependsOnSelect";
import SaveButton from "@/components/SaveButton";
import { NumberInput } from "@/components/ui/NumberInput";
import { Input } from "@/components/ui/input";
import { isRequired } from "@/lib/form";
import { usePayrollTypesDictionary } from "@/modules/payrollTypes/hooks/usePayrollTypesDictionary";
import { useTranslation } from "react-i18next";
import { ZodObject, ZodRawShape } from "zod";
import { ReadPayrollType, WritePayrollType } from "@/types/payrollType";
import { PROJECT_DEPENDENCY_ROLE } from "@/schemas/commonFields";
import PayrollTypeHoursBoundToVisitsField from "./PayrollTypeHoursBoundToVisitsField";
import PayrollTypeBlockTimesheetField from "./PayrollTypeBlockTimesheetField";

interface PayrollTypeFormProps {
  mode?: "create" | "update";
  schema?: ZodObject<ZodRawShape>;
  footer?: React.ReactNode;
}

export default function PayrollTypeForm({
  mode = "create",
  schema: schemaFromProps,
  footer,
}: PayrollTypeFormProps) {
  const { t } = useTranslation();
  const config = usePayrollTypesDictionary();
  const { Icon, ns } = config;
  const labels = config.getLabels(t);
  const { mutation, query } = useDictionaryContext<
    ReadPayrollType,
    WritePayrollType
  >();

  const schema = schemaFromProps ?? config.schema;

  return (
    <DictionaryForm>
      <PageContent
        icon={<Icon className="w-6 h-6 mr-3" />}
        title={labels.title}
        footer={footer || <SaveButton />}
      >
        <Fieldset className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field
            name="name"
            label={t(`${ns}.fields.name`)}
            required={isRequired(schema.shape.name)}
            render={({ field }) => (
              <Input
                disabled={mutation.isPending || mode === "update"}
                className="w-full"
                {...field}
              />
            )}
          />

          <Field
            name="description"
            label={t(`${ns}.fields.description`)}
            required={isRequired(schema.shape.description)}
            render={({ field }) => (
              <Input
                disabled={mutation.isPending}
                className="w-full"
                {...field}
              />
            )}
          />

          <CheckboxField
            name="split"
            label={t(`${ns}.fields.split`)}
            required={isRequired(schema.shape.split)}
            disabled={mutation.isPending}
          />

          <Field
            name="invoicing_company_id"
            label={t(`${ns}.fields.invoicing_company`)}
            required={isRequired(schema.shape.invoicing_company_id)}
            render={({ field }) => (
              <InvoicingCompanySelect
                disabled={mutation.isPending}
                defaultItem={{
                  id: query?.data?.invoicing_company_id,
                  name: query?.data?.invoicing_company,
                }}
                {...field}
              />
            )}
          />

          <Field
            name="administrative_fee"
            label={t(`${ns}.fields.administrative_fee`)}
            required={isRequired(schema.shape.administrative_fee)}
            render={({ field }) => (
              <NumberInput
                disabled={mutation.isPending}
                className="w-full"
                placeholder="0,00"
                {...field}
              />
            )}
          />

          <Field
            name="deduction_percentage"
            label={t(`${ns}.fields.deduction_percentage`)}
            required={isRequired(schema.shape.deduction_percentage)}
            render={({ field }) => (
              <NumberInput
                disabled={mutation.isPending}
                className="w-full"
                placeholder="0,00"
                {...field}
              />
            )}
          />

          <Field
            name="project_depends_on"
            label={t(`${ns}.fields.project_depends_on`)}
            required={isRequired(schema.shape.project_depends_on)}
            render={({ field }) => (
              <ProjectDependsOnSelect
                disabled={mutation.isPending || mode === "update"}
                {...field}
              />
            )}
          />

          <Field
            name="agreement_type_id"
            label={t(`${ns}.fields.agreement_type`)}
            required={isRequired(schema.shape.agreement_type_id)}
            render={({ field }) => (
              <AgreementTypeSelect
                disabled={mutation.isPending}
                defaultItem={{
                  id: query?.data?.agreement_type_id,
                  name: query?.data?.agreement_type,
                }}
                {...field}
              />
            )}
          />

          {mode === "update" &&
            query?.data?.project_depends_on === PROJECT_DEPENDENCY_ROLE && (
              <PayrollTypeHoursBoundToVisitsField
                ns={ns}
                schema={schema}
                disabled={mutation.isPending}
              />
            )}

          {mode === "update" && (
            <PayrollTypeBlockTimesheetField disabled={mutation.isPending} />
          )}
        </Fieldset>
      </PageContent>
    </DictionaryForm>
  );
}
